import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Save, Trash2, Loader2, Calendar as CalendarIcon,
  Facebook, Instagram, Linkedin, MessageCircle,
  Mail, Phone, Building2, MapPin, StickyNote,
  ArrowRightLeft, FileText, ClipboardList, UserPlus, UserCheck, Clock, Globe, Layers,
  Video, ExternalLink, Check, CheckCheck, AlertTriangle, Send, Link2, Pencil, Ban, Tag, User
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { PhoneInput, parsePhone, combinePhone } from "../components/PhoneInput";
import { CLOSED_STATUSES, daysSince, agingTone, agingLabel, labelForAction } from "../lib/leadActivity";
import MergeLeadDialog from "../components/MergeLeadDialog";
import LostReasonDialog from "../components/LostReasonDialog";
import FollowUpsCard from "../components/FollowUpsCard";
import PossibleDuplicatesBanner from "../components/PossibleDuplicatesBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Format date as "d Month" (same year) or "d Month yyyy" (different year) */
function formatDate(date) {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";
    const isCurrentYear = d.getFullYear() === new Date().getFullYear();
    return format(d, isCurrentYear ? "d MMMM" : "d MMMM yyyy");
  } catch { return ""; }
}

function formatDateTime(date) {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";
    const isCurrentYear = d.getFullYear() === new Date().getFullYear();
    return format(d, isCurrentYear ? "d MMMM, h:mm a" : "d MMMM yyyy, h:mm a");
  } catch { return ""; }
}

/**
 * Validate and format phone for WhatsApp (wa.me requires country code, digits only, no +).
 * Returns { valid, waPhone, message }
 */
function getWhatsAppPhone(phone) {
  if (!phone || phone.trim() === "") {
    return { valid: false, waPhone: "", message: "No phone number. Add a phone number first." };
  }
  const digits = phone.replace(/[^0-9]/g, "");
  if (digits.length < 8) {
    return { valid: false, waPhone: "", message: "Phone number is too short. Please add a valid country code and number." };
  }
  // If starts with 0, it's local Malaysian — auto-fix to 60
  if (digits.startsWith("0")) {
    return { valid: true, waPhone: "60" + digits.slice(1), message: "" };
  }
  // If starts with + (already stripped), check if we have a reasonable intl number
  if (digits.length >= 10) {
    return { valid: true, waPhone: digits, message: "" };
  }
  // Short number without country code — assume Malaysia
  return { valid: true, waPhone: "60" + digits, message: "" };
}

const sourceIcons = { Facebook: Facebook, Instagram: Instagram, LinkedIn: Linkedin, WhatsApp: MessageCircle };
const statusStyles = {
  New: "bg-blue-100 text-blue-700 border-blue-200",
  Contacted: "bg-amber-100 text-amber-700 border-amber-200",
  Demoed: "bg-purple-100 text-purple-700 border-purple-200",
  Subscribed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Lost: "bg-red-100 text-red-700 border-red-200",
};
const statusOrder = ["New", "Contacted", "Demoed", "Subscribed", "Lost"];

const planBadgeStyles = {
  Free: "bg-gray-100 text-gray-700 border-gray-200",
  Basic: "bg-blue-100 text-blue-700 border-blue-200",
  Plus: "bg-purple-100 text-purple-700 border-purple-200",
  Pro: "bg-amber-100 text-amber-800 border-amber-200",
};

const activityConfig = {
  lead_created: { label: "Created", icon: FileText, color: "bg-emerald-100 text-emerald-700" },
  lead_updated: { label: "Updated", icon: ClipboardList, color: "bg-blue-100 text-blue-700" },
  lead_status_changed: { label: "Status Changed", icon: ArrowRightLeft, color: "bg-purple-100 text-purple-700" },
  lead_assigned: { label: "Assigned", icon: UserPlus, color: "bg-indigo-100 text-indigo-700" },
  lead_deleted: { label: "Deleted", icon: Trash2, color: "bg-red-100 text-red-700" },
  meeting_scheduled: { label: "Meeting Scheduled", icon: Video, color: "bg-indigo-100 text-indigo-700" },
  teams_invite_sent: { label: "Teams Invite Sent", icon: Mail, color: "bg-[#5B5FC7]/10 text-[#5B5FC7]", badge: "Sent", badgeColor: "bg-blue-100 text-blue-700" },
  whatsapp_sent: { label: "WhatsApp Sent", icon: MessageCircle, color: "bg-[#25D366]/10 text-[#25D366]", badge: "Sent", badgeColor: "bg-emerald-100 text-emerald-700" },
  whatsapp_delivered: { label: "WhatsApp Delivered", icon: Check, color: "bg-[#25D366]/10 text-[#25D366]", badge: "Delivered", badgeColor: "bg-blue-100 text-blue-700" },
  whatsapp_read: { label: "WhatsApp Read", icon: CheckCheck, color: "bg-[#25D366]/10 text-[#25D366]", badge: "Read", badgeColor: "bg-emerald-100 text-emerald-700" },
  whatsapp_failed: { label: "WhatsApp Failed", icon: AlertTriangle, color: "bg-red-100 text-red-700", badge: "Failed", badgeColor: "bg-red-100 text-red-700" },
  engarage_registered: { label: "ENGARAGE Registered", icon: UserPlus, color: "bg-teal-100 text-teal-700", badge: "Registered", badgeColor: "bg-teal-100 text-teal-700" },
  engarage_subscribed: { label: "ENGARAGE Subscribed", icon: UserCheck, color: "bg-emerald-100 text-emerald-700", badge: "Subscribed", badgeColor: "bg-emerald-100 text-emerald-700" },
  engarage_matched: { label: "ENGARAGE Account Linked", icon: Link2, color: "bg-cyan-100 text-cyan-700", badge: "Linked", badgeColor: "bg-cyan-100 text-cyan-700" },
  engarage_merged: { label: "ENGARAGE Account Merged", icon: Link2, color: "bg-teal-100 text-teal-700", badge: "Merged", badgeColor: "bg-teal-100 text-teal-700" },
  daily_summary_sent: { label: "Daily Summary Sent", icon: Mail, color: "bg-gray-100 text-gray-600" },
};

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [lead, setLead] = useState(null);
  const [activity, setActivity] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState({});
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTime, setMeetingTime] = useState("10:00");
  const [meetingDuration, setMeetingDuration] = useState("60");
  const [meetingNotes, setMeetingNotes] = useState("");
  const [schedulingMeeting, setSchedulingMeeting] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergePresetSourceId, setMergePresetSourceId] = useState(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planSelection, setPlanSelection] = useState("Pro");
  const [planSubDate, setPlanSubDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostDialogMode, setLostDialogMode] = useState("create"); // "create" | "edit"
  const [usageRefreshing, setUsageRefreshing] = useState(false);

  // Trigger ENGARAGE Workshop Usage API on lead detail open to surface the latest transaction count.
  // Falls back to the cached value if outbound URL isn't configured / call fails.
  const refreshEngarageUsage = useCallback(async () => {
    if (!id) return;
    setUsageRefreshing(true);
    try {
      const r = await axios.post(`${API}/leads/${id}/engarage/refresh-usage`);
      if (r?.data) {
        setLead((prev) => prev ? {
          ...prev,
          engarage_transactions: r.data.transaction_count ?? prev.engarage_transactions,
          engarage_usage_updated_at: r.data.updated_at || prev.engarage_usage_updated_at,
        } : prev);
      }
    } catch (e) { /* graceful fail */ }
    setUsageRefreshing(false);
  }, [id]);

  const fetchLead = useCallback(async () => {
    try {
      const [leadRes, actRes, teamRes] = await Promise.all([
        axios.get(`${API}/leads/${id}`),
        axios.get(`${API}/leads/${id}/activity`),
        axios.get(`${API}/team`),
      ]);
      setLead(leadRes.data);
      setActivity(actRes.data);
      setTeamMembers(teamRes.data);
      setForm({
        name: leadRes.data.name || "",
        email: leadRes.data.email || "",
        phone: leadRes.data.phone || "",
        company: leadRes.data.company || "",
        location: leadRes.data.location || "",
        source: leadRes.data.source || "Facebook",
        status: leadRes.data.status || "New",
        notes: leadRes.data.notes || "",
        assigned_to: leadRes.data.assigned_to || "",
        follow_up_date: leadRes.data.follow_up_date ? new Date(leadRes.data.follow_up_date) : null,
        custom_fields: leadRes.data.custom_fields || null,
        subscription_plan: leadRes.data.subscription_plan || null,
        lost_reason: leadRes.data.lost_reason || null,
        lost_not_actual_workshop: leadRes.data.lost_not_actual_workshop || false,
        lost_remark: leadRes.data.lost_remark || null,
        preferred_language: leadRes.data.preferred_language || "ms",
        interested_plan: leadRes.data.interested_plan || null,
        owner_pic_name: leadRes.data.owner_pic_name || "",
        wa_optout: !!leadRes.data.wa_optout,
      });
    } catch {
      toast.error("Lead not found");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchLead(); }, [fetchLead]);
  // Trigger ENGARAGE Workshop Usage refresh once the lead is identified
  useEffect(() => { if (id) refreshEngarageUsage(); }, [id, refreshEngarageUsage]);

  // Detect unsaved changes
  const hasChanges = lead && (
    form.name !== (lead.name || "") ||
    form.email !== (lead.email || "") ||
    form.phone !== (lead.phone || "") ||
    form.company !== (lead.company || "") ||
    form.location !== (lead.location || "") ||
    form.source !== (lead.source || "Facebook") ||
    form.status !== (lead.status || "New") ||
    form.notes !== (lead.notes || "") ||
    form.assigned_to !== (lead.assigned_to || "") ||
    (form.follow_up_date ? format(form.follow_up_date, "yyyy-MM-dd") : null) !== (lead.follow_up_date || null) ||
    JSON.stringify(form.custom_fields || null) !== JSON.stringify(lead.custom_fields || null) ||
    (form.lost_reason || null) !== (lead.lost_reason || null) ||
    !!form.lost_not_actual_workshop !== !!lead.lost_not_actual_workshop ||
    (form.lost_remark || null) !== (lead.lost_remark || null) ||
    (form.preferred_language || "ms") !== (lead.preferred_language || "ms") ||
    (form.interested_plan || null) !== (lead.interested_plan || null) ||
    (form.owner_pic_name || "") !== (lead.owner_pic_name || "") ||
    (!!form.wa_optout) !== (!!lead.wa_optout)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...form,
        follow_up_date: form.follow_up_date ? format(form.follow_up_date, "yyyy-MM-dd") : null,
        assigned_to: form.assigned_to || null,
      };
      await axios.put(`${API}/leads/${id}`, data);
      toast.success("Lead updated");
      fetchLead();
    } catch {
      toast.error("Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  const handleScheduleMeeting = async () => {
    if (!meetingDate || !meetingTime) {
      toast.error("Please select a date and time");
      return;
    }
    setSchedulingMeeting(true);
    try {
      const dateTime = `${meetingDate}T${meetingTime}:00`;
      const res = await axios.post(`${API}/leads/${id}/schedule-meeting`, {
        lead_id: id,
        meeting_date: dateTime,
        duration_minutes: parseInt(meetingDuration),
        timezone: "Asia/Kuala_Lumpur",
        language: form.preferred_language || lead?.preferred_language || "ms",
        notes: meetingNotes || null,
      });
      toast.success("Meeting scheduled! Invite sent to lead.");
      setShowMeetingForm(false);
      setMeetingDate("");
      setMeetingTime("10:00");
      setMeetingDuration("60");
      setMeetingNotes("");
      fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule meeting");
    } finally {
      setSchedulingMeeting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D0D5B]" />
      </div>
    );
  }

  if (!lead) return null;

  const SourceIcon = sourceIcons[lead.source] || MessageCircle;

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto animate-fade-in" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/leads")} className="text-gray-500 hover:text-gray-900 -ml-2" data-testid="back-to-leads">
            <ArrowLeft className="w-4 h-4 mr-1" /> Leads
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && <span className="text-sm font-medium text-red-500" data-testid="unsaved-changes-warning">Changes not saved</span>}
          <Button size="sm" variant="outline" onClick={() => navigate("/leads")} className="rounded-lg">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="save-lead-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
          {currentUser?.role === "super_admin" && (
            <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)} className="text-red-600 border-red-200 hover:bg-red-50 rounded-lg" data-testid="delete-lead-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Lead Header Card */}
      <Card className="border border-gray-200 rounded-xl bg-white mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center gap-4">
            {/* Left — Identity */}
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-14 h-14 rounded-xl bg-[#0D0D5B]/10 flex items-center justify-center shrink-0">
                <span className="text-xl font-semibold text-[#0D0D5B]">{lead.name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="text-xl font-semibold border-gray-300 rounded-lg h-10" data-testid="detail-name-input" />
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><SourceIcon className="w-3.5 h-3.5" />{lead.source}</span>
                  {lead.integration_name && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded" data-testid="detail-integration-name">{lead.integration_name}</span>}
                  <span>Added {(() => { try { return format(new Date(lead.created_at), "d MMMM yyyy, h:mm a"); } catch { return ""; } })()}</span>
                </div>
              </div>
            </div>

            {/* Center — ENGARAGE Usage (always rendered; truly centered, fixed width so it doesn't push the sides) */}
            <div className="flex justify-center sm:justify-self-center">
              <div className="flex flex-col items-center px-4 py-2 rounded-xl bg-amber-50/60 border border-amber-200 w-[180px]" data-testid="lead-usage-block">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">ENGARAGE Usage</div>
                {usageRefreshing ? (
                  <div className="text-sm font-bold leading-tight mt-0.5">
                    <span className="inline-flex items-center gap-1.5 text-amber-700"><Loader2 className="w-4 h-4 animate-spin" /> Refreshing…</span>
                  </div>
                ) : typeof lead?.engarage_transactions === "number" ? (
                  <>
                    <div className="text-lg font-bold text-amber-900 leading-tight mt-0.5 flex items-center gap-1.5" data-testid="lead-usage-headline">
                      <span className="tabular-nums">{lead.engarage_transactions.toLocaleString()}</span>
                      <span className="text-xs font-semibold text-amber-700">Transaction{lead.engarage_transactions === 1 ? "" : "s"}</span>
                    </div>
                    {lead?.engarage_usage_updated_at && (
                      <div className="text-[10px] text-amber-600/80 mt-0.5">
                        Updated {(() => { try { return format(new Date(lead.engarage_usage_updated_at), "d MMM, h:mm a"); } catch { return ""; } })()}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[11px] text-amber-700/80 leading-tight mt-1 text-center" data-testid="lead-usage-empty">
                    No usage data yet
                  </div>
                )}
              </div>
            </div>

            {/* Right — Status Pipeline + Aging */}
            <div className="flex flex-col items-end gap-2 sm:justify-self-end min-w-0">
              <div className="flex items-center gap-1" data-testid="status-pipeline">
                {statusOrder.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      if (s === "Subscribed" && form.status !== "Subscribed") {
                        // Open plan picker before flipping status
                        setPlanSelection(lead?.custom_fields?.Plan || lead?.subscription_plan || "Pro");
                        const existingDate = lead?.custom_fields?.["Subscription Date"];
                        try {
                          setPlanSubDate(existingDate ? format(new Date(existingDate), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"));
                        } catch {
                          setPlanSubDate(format(new Date(), "yyyy-MM-dd"));
                        }
                        setPlanDialogOpen(true);
                        return;
                      }
                      if (s === "Lost" && form.status !== "Lost") {
                        // Open lost-reason dialog before flipping status
                        setLostDialogMode("create");
                        setLostDialogOpen(true);
                        return;
                      }
                      setForm({ ...form, status: s });
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer ${
                      form.status === s ? statusStyles[s] + " ring-2 ring-offset-1 ring-current" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                    data-testid={`status-btn-${s.toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {/* Aging + Last Activity (hidden for closed deals) */}
              {!CLOSED_STATUSES.has(form.status) && (() => {
                const ts = lead?.last_activity_at || lead?.created_at;
                const days = daysSince(ts);
                return (
                  <div className="flex items-center gap-2 text-xs" data-testid="lead-aging">
                    <span className="text-gray-500">Aging:</span>
                    <span className={`font-semibold ${agingTone(days)}`}>{agingLabel(days)}</span>
                    {lead?.last_activity_action && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="text-gray-600">Last activity: <span className="text-gray-800 font-medium">{labelForAction(lead.last_activity_action)}</span></span>
                        {lead?.last_activity_by && lead.last_activity_by !== "System" && (
                          <span className="text-gray-400">by {lead.last_activity_by}</span>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
              {/* Subscription Plan Badge (shown below status when Subscribed) */}
              {form.status === "Subscribed" && (form?.custom_fields?.Plan || form?.subscription_plan || lead?.custom_fields?.Plan || lead?.subscription_plan) && (
                <div className="flex items-center gap-2 text-xs flex-wrap justify-end" data-testid="subscription-plan-display">
                  <span className="text-gray-500">Plan:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${planBadgeStyles[(form?.custom_fields?.Plan || form?.subscription_plan || lead?.custom_fields?.Plan || lead?.subscription_plan)] || "bg-gray-100 text-gray-600 border-gray-200"}`} data-testid="plan-badge">
                    {form?.custom_fields?.Plan || form?.subscription_plan || lead?.custom_fields?.Plan || lead?.subscription_plan}
                  </span>
                  {(() => {
                    const subDate = form?.custom_fields?.["Subscription Date"] || lead?.custom_fields?.["Subscription Date"] || lead?.subscribed_at;
                    if (!subDate) return null;
                    try {
                      return <span className="text-gray-400">since {format(new Date(subDate), "d MMM yyyy")}</span>;
                    } catch { return null; }
                  })()}
                  {lead?.subscribed_by && (
                    <span className="text-gray-400 inline-flex items-center gap-1" data-testid="subscribed-by-display">
                      <span className="text-gray-300">·</span>
                      by <span className={`font-medium ${lead.subscribed_via === "engarage" ? "text-teal-700" : "text-gray-700"}`}>{lead.subscribed_by}</span>
                      {lead.subscribed_via && (
                        <span className={`px-1.5 py-0 rounded-full text-[9.5px] font-semibold border ${lead.subscribed_via === "engarage" ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                          {lead.subscribed_via === "engarage" ? "API" : "Manual"}
                        </span>
                      )}
                    </span>
                  )}
                  {/* Previous-plan / downgrade badge */}
                  {lead?.previous_subscription_plan && lead.previous_subscription_plan !== (lead?.subscription_plan || lead?.custom_fields?.Plan) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border bg-red-50 text-red-700 border-red-200" data-testid="downgrade-badge">
                      {(lead?.subscription_plan === "Free" || lead?.custom_fields?.Plan === "Free") ? "Downgraded" : "Changed"} from {lead.previous_subscription_plan}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — Lead Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Possible Duplicates Banner — shown only when backend flags potential matches */}
          {lead?.possible_duplicates?.length > 0 && lead?.custom_fields?._dup_dismissed !== "1" && (
            <PossibleDuplicatesBanner
              lead={lead}
              onMergeClick={(candidate) => { setMergePresetSourceId(candidate?.id || null); setMergeOpen(true); }}
              onDismiss={fetchLead}
            />
          )}

          <Card className="border border-gray-200 rounded-xl bg-white">
            <CardContent className="p-6">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Lead Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-gray-300 rounded-lg" data-testid="detail-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</Label>
                  <PhoneInput value={form.phone} onChange={(val) => setForm({ ...form, phone: val })} data-testid="detail-phone-input" />
                  <label className="flex items-center gap-2 mt-1.5 cursor-pointer select-none" data-testid="wa-optout-row">
                    <input
                      type="checkbox"
                      checked={!!form.wa_optout}
                      onChange={(e) => setForm({ ...form, wa_optout: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
                      data-testid="wa-optout-toggle"
                    />
                    <span className="text-[11px] text-gray-500">Opt-out of WhatsApp auto-messages for this lead</span>
                  </label>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 flex items-center gap-1"><Globe className="w-3 h-3" /> Preferred Language</Label>
                  <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg h-10 items-center" data-testid="lead-language-toggle">
                    {[{id:"ms",label:"Bahasa Melayu"},{id:"en",label:"English"}].map((l) => {
                      const active = (form.preferred_language || "ms") === l.id;
                      return (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => setForm({ ...form, preferred_language: l.id })}
                          className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-colors ${active ? "bg-white text-[#0D0D5B] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                          data-testid={`lead-lang-${l.id}`}
                        >
                          {l.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-400">Used for WhatsApp & email language.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 flex items-center gap-1"><Tag className="w-3 h-3" /> Interested In</Label>
                  <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg h-10 items-center" data-testid="interested-plan-toggle">
                    {[{id:"Basic",color:"text-blue-600"},{id:"Plus",color:"text-purple-600"},{id:"Pro",color:"text-amber-600"}].map((p) => {
                      const active = form.interested_plan === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setForm({ ...form, interested_plan: active ? null : p.id })}
                          className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-colors ${active ? `bg-white shadow-sm ${p.color}` : "text-gray-500 hover:text-gray-700"}`}
                          data-testid={`interested-plan-${p.id.toLowerCase()}`}
                        >
                          {p.id}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-400">{form.interested_plan ? `Click ${form.interested_plan} again to clear` : "ENGARAGE plan the lead expressed interest in."}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Follow-ups Card — now positioned above Additional Information */}
          <FollowUpsCard lead={lead} statusColors={statusStyles} onChange={fetchLead} />

          <Card className="border border-gray-200 rounded-xl bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Additional Information
                </h2>
                {lead?.engarage_linked ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 max-w-[60%] truncate" data-testid="engarage-matched-badge" title={`Linked to ENGARAGE (${lead.engarage_email})`}>
                    <Link2 className="w-3 h-3 shrink-0" /> Linked to ENGARAGE ({lead.engarage_email})
                  </span>
                ) : lead?.email && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setMergeOpen(true)}
                    className="text-xs border-teal-300 text-teal-700 hover:bg-teal-50 rounded-lg h-7"
                    data-testid="match-engarage-btn"
                  >
                    <Link2 className="w-3 h-3 mr-1.5" />
                    Match ENGARAGE Account
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 items-start">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs text-gray-500">Source</Label>
                    {lead.integration_name && (
                      <span className="text-[10px] text-gray-400 truncate max-w-[60%]" title={`via ${lead.integration_name}`}>via {lead.integration_name}</span>
                    )}
                  </div>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger className="border-gray-300 rounded-lg" data-testid="detail-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Facebook", "Instagram", "LinkedIn", "WhatsApp", "SaleSmartly", "ENGARAGE", "API", "Direct", "Referral", "Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 flex items-center gap-1"><User className="w-3 h-3" /> Owner / PIC Name</Label>
                  <Input
                    value={form.owner_pic_name || ""}
                    onChange={(e) => setForm({ ...form, owner_pic_name: e.target.value })}
                    placeholder="e.g. Encik Azman (Workshop owner)"
                    className="border-gray-300 rounded-lg"
                    data-testid="detail-owner-pic-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company</Label>
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="border-gray-300 rounded-lg" data-testid="detail-company-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border-gray-300 rounded-lg" data-testid="detail-location-input" />
                </div>
                {/* Custom / ENGARAGE-synced fields, inline */}
                {lead.custom_fields && Object.entries(lead.custom_fields).filter(([k]) => k !== "_dup_dismissed").map(([key, value]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-xs text-gray-500 flex items-center gap-1">
                      <Globe className="w-3 h-3" /> {key}
                    </Label>
                    <Input
                      value={form.custom_fields?.[key] ?? value ?? ""}
                      onChange={(e) => setForm({ ...form, custom_fields: { ...(form.custom_fields || lead.custom_fields), [key]: e.target.value } })}
                      className="border-gray-300 rounded-lg"
                      data-testid={`custom-field-${key.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="mt-6 pt-6 border-t border-gray-100 space-y-1.5">
                <Label className="text-xs text-gray-500 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-gray-300 rounded-lg min-h-[100px]" data-testid="detail-notes" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column — Assigned To + Activity Timeline */}
        <div className="space-y-6">
          {/* Assigned To Card */}
          <Card className="border border-gray-200 rounded-xl bg-white" data-testid="assigned-to-card">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <UserPlus className="w-4 h-4" /> Assigned To
              </h2>
              <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "unassigned" ? "" : v })}>
                <SelectTrigger className="border-gray-300 rounded-lg" data-testid="detail-assigned-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.filter(m => m.assignable !== false).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Lost Details Card — shown only when status is Lost */}
          {form.status === "Lost" && (form.lost_reason || lead?.lost_reason) && (
            <Card className="border border-red-200 rounded-xl bg-red-50" data-testid="lost-details-box">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-sm font-semibold text-red-700 uppercase tracking-wider flex items-center gap-2">
                    <Ban className="w-4 h-4" /> Lost Details
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setLostDialogMode("edit"); setLostDialogOpen(true); }}
                    className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-xs font-medium"
                    data-testid="edit-lost-details-btn"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                </div>
                <div className="grid grid-cols-[72px_1fr] gap-y-2 text-xs text-gray-700">
                  <span className="text-gray-500">Reason:</span>
                  <span className="font-medium" data-testid="lost-reason-display">{form.lost_reason || lead?.lost_reason}</span>
                  {(form.lost_not_actual_workshop || lead?.lost_not_actual_workshop) && (
                    <>
                      <span className="text-gray-500">Flag:</span>
                      <span className="inline-flex items-center gap-1" data-testid="lost-not-actual-display">
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold border bg-amber-50 text-amber-800 border-amber-200">Not an actual workshop</span>
                      </span>
                    </>
                  )}
                  {(form.lost_remark || lead?.lost_remark) && (
                    <>
                      <span className="text-gray-500">Remark:</span>
                      <span className="whitespace-pre-wrap break-words" data-testid="lost-remark-display">{form.lost_remark || lead?.lost_remark}</span>
                    </>
                  )}
                  {lead?.lost_by && (
                    <>
                      <span className="text-gray-500">By:</span>
                      <span data-testid="lost-by-display">
                        <span className="font-medium text-gray-800">{lead.lost_by}</span>
                        {lead?.lost_at && (() => {
                          try { return <span className="text-gray-400"> · {format(new Date(lead.lost_at), "d MMM yyyy")}</span>; } catch { return null; }
                        })()}
                      </span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Schedule Meeting Card */}
          <Card className="border border-gray-200 rounded-xl bg-white" data-testid="meeting-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                  <Video className="w-4 h-4" /> Meetings
                </h2>
                {!showMeetingForm && (
                  <Button variant="outline" size="sm" onClick={() => setShowMeetingForm(true)} className="text-xs h-7 border-[#0D0D5B] text-[#0D0D5B] hover:bg-[#0D0D5B]/5" data-testid="schedule-meeting-btn">
                    <CalendarIcon className="w-3 h-3 mr-1" /> Schedule Demo
                  </Button>
                )}
              </div>

              {showMeetingForm && (
                <div className="space-y-3 p-3 bg-[#0D0D5B]/[0.03] rounded-lg border border-[#0D0D5B]/10" data-testid="meeting-form">
                  {/* Date — full width to fit "12 May 2026" without truncation */}
                  <div>
                    <Label className="text-xs text-gray-500">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal border-gray-300 rounded-lg text-sm h-9 overflow-hidden"
                          data-testid="meeting-date-input"
                        >
                          <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {meetingDate ? format(new Date(meetingDate), "EEE, d MMM yyyy") : "Pick a date"}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={meetingDate ? new Date(meetingDate) : undefined}
                          onSelect={(d) => setMeetingDate(d ? format(d, "yyyy-MM-dd") : "")}
                          disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Time + Duration share one row */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-500">Time</Label>
                      <Input
                        type="time"
                        value={meetingTime}
                        onChange={(e) => setMeetingTime(e.target.value)}
                        className="border-gray-300 rounded-lg text-sm h-9"
                        data-testid="meeting-time-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Duration</Label>
                      <Select value={meetingDuration} onValueChange={setMeetingDuration}>
                        <SelectTrigger className="border-gray-300 rounded-lg text-sm h-9" data-testid="meeting-duration-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="90">1.5 hours</SelectItem>
                          <SelectItem value="120">2 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Notes (optional)</Label>
                    <Input value={meetingNotes} onChange={(e) => setMeetingNotes(e.target.value)} placeholder="Any specific topics to discuss..." className="border-gray-300 rounded-lg text-sm h-9" data-testid="meeting-notes-input" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => setShowMeetingForm(false)} className="flex-1 text-xs h-8">Cancel</Button>
                    <Button size="sm" onClick={handleScheduleMeeting} disabled={schedulingMeeting || !meetingDate} className="flex-1 text-xs h-8 bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white" data-testid="confirm-schedule-btn">
                      {schedulingMeeting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Video className="w-3 h-3 mr-1" />}
                      {schedulingMeeting ? "Creating..." : "Create Teams Meeting"}
                    </Button>
                  </div>
                  <p className="text-[11px] text-gray-400 text-center">
                    Calendar invite with Teams link will be sent to {lead.email || "the lead"}
                  </p>
                </div>
              )}

              {/* Scheduled meetings list */}
              {lead.meetings && lead.meetings.length > 0 ? (
                <div className="space-y-2 mt-2">
                  {lead.meetings.map((m) => (
                    <div key={m.id} className="p-3 bg-white border border-gray-200 rounded-lg" data-testid={`meeting-${m.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{m.subject}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {(() => { try { return formatDate(new Date(m.start)); } catch { return m.start; } })()} &middot; {(() => { try { return format(new Date(m.start), "h:mm a"); } catch { return ""; } })()} — {(() => { try { return format(new Date(m.end), "h:mm a"); } catch { return ""; } })()}
                          </p>
                          {m.attendees && m.attendees.length > 0 && (
                            <p className="text-[11px] text-gray-400 mt-1 truncate">
                              {m.attendees.map(a => a.name || a.email).join(", ")}
                            </p>
                          )}
                          {m.notes && <p className="text-[11px] text-gray-400 mt-0.5 italic">{m.notes}</p>}
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0 ml-2">
                          {m.join_url && (
                            <a href={m.join_url} target="_blank" rel="noopener noreferrer" data-testid={`join-meeting-${m.id}`}>
                              <Button variant="outline" size="sm" className="text-xs h-7 w-full border-[#5B5FC7] text-[#5B5FC7] hover:bg-[#5B5FC7]/5">
                                <ExternalLink className="w-3 h-3 mr-1" /> Join
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !showMeetingForm && (
                <p className="text-xs text-gray-400 text-center py-3">No meetings scheduled</p>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card className="border border-gray-200 rounded-xl bg-white">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Activity
              </h2>
              {activity.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No activity yet</p>
              ) : (
                <div className="relative space-y-0" data-testid="activity-timeline">
                  {/* Timeline line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-200" />
                  {activity.map((log, i) => {
                    const cfg = activityConfig[log.action] || { label: log.action, icon: ClipboardList, color: "bg-gray-100 text-gray-700" };
                    const Icon = cfg.icon;
                    const isWaOrTeams = ["whatsapp_sent","whatsapp_delivered","whatsapp_read","whatsapp_failed","teams_invite_sent","engarage_registered","engarage_subscribed","engarage_matched"].includes(log.action);
                    return (
                      <div key={log.id} className="relative flex gap-3 pb-4" data-testid={`activity-${log.id}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${cfg.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="pt-1 min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs font-medium text-gray-900">{cfg.label}</p>
                            {cfg.badge && (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cfg.badgeColor}`} data-testid={`badge-${log.action}`}>
                                {cfg.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500">by {log.user_name} &middot; {formatDateTime(log.created_at || log.timestamp)}</p>
                          {/* WhatsApp/Teams specific details */}
                          {isWaOrTeams && log.details && (
                            <div className="mt-1 space-y-0.5">
                              {log.details.phone && (
                                <p className="text-[11px] text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {log.details.phone}</p>
                              )}
                              {log.details.recipients && (
                                <p className="text-[11px] text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {log.details.recipients.join(", ")}</p>
                              )}
                              {log.details.subject && (
                                <p className="text-[11px] text-gray-500 truncate max-w-[200px]">{log.details.subject}</p>
                              )}
                              {log.details.join_url && (
                                <a href={log.details.join_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Join Link</a>
                              )}
                              {log.details.error && (
                                <p className="text-[11px] text-red-500">{log.details.error}</p>
                              )}
                              {log.details.error_title && (
                                <p className="text-[11px] text-red-500">{log.details.error_title}</p>
                              )}
                            </div>
                          )}
                          {/* Standard diff details */}
                          {!isWaOrTeams && log.details && Object.entries(log.details).map(([k, v]) => {
                            // Hide noisy / verbose fields from the timeline — they're already captured server-side
                            const NOISY_KEYS = ["custom_fields", "follow_ups", "reschedule_history", "lost_at", "subscribed_at", "lost_by", "subscribed_by", "subscribed_via", "lost_remark", "lost_not_actual_workshop", "preferred_language"];
                            if (NOISY_KEYS.includes(k)) return null;
                            if (v && typeof v === "object" && "old" in v && "new" in v) {
                              const fmtVal = (val) => {
                                if (val == null || val === "") return "—";
                                if (typeof val === "object") return null; // skip JSON dumps
                                const s = String(val);
                                return s.length > 60 ? s.slice(0, 57) + "…" : s;
                              };
                              const oldStr = fmtVal(v.old);
                              const newStr = fmtVal(v.new);
                              if (oldStr === null || newStr === null) return null;
                              return (
                                <p key={k} className="text-[11px] text-gray-500 mt-0.5">
                                  <span className="capitalize">{k.replace(/_/g, " ")}: </span>
                                  <span className="line-through text-red-400">{oldStr}</span>
                                  {" → "}
                                  <span className="text-emerald-600">{newStr}</span>
                                </p>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Lead</DialogTitle>
            <DialogDescription>Are you sure you want to delete <strong>{lead.name}</strong>? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">Delete Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ENGARAGE Manual Merge Dialog */}
      <MergeLeadDialog
        open={mergeOpen}
        onOpenChange={(o) => { setMergeOpen(o); if (!o) setMergePresetSourceId(null); }}
        currentLead={lead}
        teamMembers={teamMembers}
        onMerged={fetchLead}
        presetSourceId={mergePresetSourceId}
      />

      {/* Lost Reason Dialog */}
      <LostReasonDialog
        open={lostDialogOpen}
        onOpenChange={setLostDialogOpen}
        mode={lostDialogMode}
        initial={{
          lost_reason: form.lost_reason || lead?.lost_reason,
          lost_not_actual_workshop: form.lost_not_actual_workshop ?? lead?.lost_not_actual_workshop,
          lost_remark: form.lost_remark || lead?.lost_remark,
        }}
        onConfirm={(payload) => {
          if (lostDialogMode === "edit") {
            setForm({ ...form, ...payload });
            toast.success("Lost details updated. Don't forget to Save.");
          } else {
            setForm({ ...form, status: "Lost", ...payload });
            toast.success("Marked as Lost. Don't forget to Save.");
          }
          setLostDialogOpen(false);
        }}
      />

      {/* Subscribe — Plan & Date selector */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="subscribe-plan-dialog">
          <DialogHeader>
            <DialogTitle>Mark as Subscribed</DialogTitle>
            <DialogDescription>Select the plan and the date the lead subscribed on.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-gray-500">Plan</Label>
              <Select value={planSelection} onValueChange={setPlanSelection}>
                <SelectTrigger className="border-gray-300 rounded-lg text-sm h-9 mt-1" data-testid="subscribe-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Free">Free</SelectItem>
                  <SelectItem value="Basic">Basic</SelectItem>
                  <SelectItem value="Plus">Plus</SelectItem>
                  <SelectItem value="Pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Subscription Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal border-gray-300 rounded-lg text-sm h-9" data-testid="subscribe-date-input">
                    <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
                    {planSubDate ? format(new Date(planSubDate), "d MMMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={planSubDate ? new Date(planSubDate) : undefined}
                    onSelect={(d) => d && setPlanSubDate(format(d, "yyyy-MM-dd"))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)} data-testid="subscribe-cancel-btn">Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="subscribe-confirm-btn"
              onClick={() => {
                const subIso = planSubDate ? new Date(planSubDate + "T00:00:00Z").toISOString() : new Date().toISOString();
                const updatedCf = {
                  ...(lead?.custom_fields || {}),
                  Plan: planSelection,
                  "Subscription Date": subIso,
                };
                setForm({
                  ...form,
                  status: "Subscribed",
                  subscription_plan: planSelection,
                  custom_fields: updatedCf,
                });
                setPlanDialogOpen(false);
                toast.success(`Marked as Subscribed (${planSelection}). Don't forget to Save.`);
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
